/**
 * Container for a mobile filter option item.
 *
 * Wraps the onToggle callback with useCallback to produce a stable
 * onPress reference, avoiding inline functions in the parent's JSX.
 * Follows the same pattern as SortOptionItemContainer.
 * @module features/scouting/components/MobileFilterSheet/MobileFilterOption/MobileFilterOptionContainer
 */

import React, { memo, useCallback } from "react";

import MobileFilterOptionView from "./MobileFilterOptionView";

/** Props for MobileFilterOptionContainer */
interface MobileFilterOptionContainerProps {
  /** Unique ID for this filter option */
  readonly id: string;
  /** Display label for the filter option */
  readonly label: string;
  /** Whether this option is currently selected */
  readonly isSelected: boolean;
  /** Callback when the option is toggled — receives the option ID */
  readonly onToggle: (id: string) => void;
}

/**
 * Wraps the toggle callback for a single filter option item.
 * @param root0 - Component props
 * @param root0.id - Option ID
 * @param root0.label - Option label
 * @param root0.isSelected - Whether option is selected
 * @param root0.onToggle - Toggle callback
 */
const MobileFilterOptionContainer = ({
  id,
  label,
  isSelected,
  onToggle,
}: MobileFilterOptionContainerProps) => {
  const handlePress = useCallback(() => {
    onToggle(id);
  }, [onToggle, id]);

  return (
    <MobileFilterOptionView
      label={label}
      isSelected={isSelected}
      onPress={handlePress}
    />
  );
};

MobileFilterOptionContainer.displayName = "MobileFilterOptionContainer";

export default memo(MobileFilterOptionContainer);
